/* eslint-disable prettier/prettier */
/* eslint-disable import/no-cycle */
import React, { ReactElement } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { omit } from 'lodash';
import { SeoMetaDataGraphQL } from '../../models/Contentful/types/siteTemplateGraphQL';
import WithPage from '../../HOC/Page/WithPage';
import buildHelpers from '../../utils/buildHelpers';
import contentfulClientGraphQLI from '../../models/ContentfulGraphQLi';
import { IGraphQlQueryOption } from '../../models/ContentfulGraphQLi/@types';
import { OVERVIEW_TAB } from '../../Constants/variables';
import { TabsBladeProps } from '../../blades/tabs/@types';
import {
  ChallengeNestedSlugs,
  ChallengeSlugNamesQuery,
  IChallengeProps,
  IChallengeProps as PageTypeChallenge,
  IChallengeQueryData,
  IChallengeWidgetItems,
  TabWidgetKeys,
} from '../../models/Challenge/@types';
import MainLayout from '../../layout/MainLayout';
import { getPageData, getSiteData } from '../../models/Contentful';
import utils from '../../utils';
import type { Page } from '../../models/Contentful/types';
import { CType, IStaticPageProps, TPageData } from '../../models/Contentful/types';
import { LayoutPage } from '../../types/page';
import ChallengeDetail from '../../components/ChallengeDetail';
import { ChallengeTabs, getChallengeUrl } from '../../models/Challenge';
import { getTabSlugsGraphQL, getTabsWidget } from '../../utils/helpers';

const CACHED_CHALLENGES: Record<string, Page & IChallengeProps> = {};

const ChallengeDetailPage: LayoutPage<IStaticPageProps<PageTypeChallenge>> = ({ page }) => {
  return <ChallengeDetail {...(page as PageTypeChallenge)} />;
};

ChallengeDetailPage.getLayout = function getLayout(
  page: ReactElement,
  props: IStaticPageProps<PageTypeChallenge>,
) {
  return <MainLayout {...props}>{page}</MainLayout>;
};

const getSiteMapUpdatePage = (
  slugs: string[],
  date: string,
  seoMetadata: SeoMetaDataGraphQL,
): CType<Page> => {
  return {
    fields: {
      directory: '',
      slug: ['challenges', ...slugs].join('/'),
      updatedDate: date,
      entryId: 'challenge',
      seoMetadataGraphQL: seoMetadata,
    },
    sys: {
      updatedAt: date,
      contentType: { sys: { id: 'challenge', type: 'Entry' } },
    },
  };
};

interface TChallengeSlugs {
  items: [{ slug: string }];
}

export const getStaticPaths: GetStaticPaths = async () => {
  const { challengeSlugs }: { challengeSlugs: TChallengeSlugs } = await contentfulClientGraphQLI({
    query: ChallengeSlugNamesQuery,
  });

  let slugsGraphQL: { params: { slug: string[] } }[] = [];
  const sitemapUpdatePagesGraphQL: CType<Page>[] = [];
  await Promise.all(
    challengeSlugs.items.map(async ({ slug }) => {
      const query: IGraphQlQueryOption = {
        query: ChallengeNestedSlugs,
        variables: {
          slug,
        },
      };
      const { challengeCollection }: { challengeCollection: IChallengeQueryData } =
        await contentfulClientGraphQLI(query);

      ChallengeTabs.forEach(tab => {
        const widgets = (challengeCollection.items[0][tab.widgetKey].items ??
          []) as IChallengeWidgetItems[];
        slugsGraphQL.push({
          params: { slug: [slug, tab.slug] },
        });
        sitemapUpdatePagesGraphQL.push(
          getSiteMapUpdatePage(
            [slug, tab.slug],
            challengeCollection.items[0].sys.publishedAt,
            challengeCollection.items[0].seoMetadata,
          ),
        );
        const nestedTabSlugs = getTabSlugsGraphQL(widgets);
        if (nestedTabSlugs) {
          nestedTabSlugs.forEach(nestedTabSlug => {
            slugsGraphQL.push({
              params: { slug: [slug, tab.slug, nestedTabSlug] },
            });
            sitemapUpdatePagesGraphQL.push(
              getSiteMapUpdatePage(
                [slug, tab.slug, nestedTabSlug],
                challengeCollection.items[0].sys.publishedAt,
                challengeCollection.items[0].seoMetadata,
              ),
            );
          });
        }
      });
    }),
  );

  const { shouldBuildClean, entrySlugs } =
    await buildHelpers.getContentEntriesSlug<PageTypeChallenge>(
      {
        content_type: 'challenge',
      },
      {
        contentType: 'challenge',
      },
    );
  const filterSlug: { params: { slug: string[] } }[] = slugsGraphQL;
  const filteredSlugData: { params: { slug: string[] } }[] = [];
  if (!shouldBuildClean && entrySlugs) {
    entrySlugs.forEach(entrySlug => {
      entrySlug?.params?.slug.forEach(slugValueData => {
        slugsGraphQL?.forEach(
          slug => slug?.params?.slug.includes(slugValueData) && filteredSlugData.push(slug),
        );
      });
    });
  }

  slugsGraphQL = utils.filterUndefinedSlugs(slugsGraphQL);
  buildHelpers.writePagesMeta(sitemapUpdatePagesGraphQL);
  const buildPage = filteredSlugData.length === 0 ? filterSlug : filteredSlugData;

  return {
    paths: buildPage,
    fallback: false,
  };
};

const setCurrentTab = (slugs: string[], challenge: PageTypeChallenge) => {
  const tabSlug = slugs[1];
  const currentTab = ChallengeTabs.find(tab => tab.slug === tabSlug);
  if (!currentTab) return;
  challenge.currentTab = currentTab;
  const nestedTabs = getTabsWidget(
    challenge[currentTab?.widgetKey ?? OVERVIEW_TAB] ?? ([] as CType<unknown>[]),
  );
  if (nestedTabs) nestedTabs.fields.tabUrl = getChallengeUrl(challenge, currentTab);
};

const setNestedTab = (slugs: string[], challenge: PageTypeChallenge) => {
  const nestedTabSlug = slugs[2];
  setCurrentTab(slugs, challenge);
  const { currentTab } = challenge;
  const nestedTabs = getTabsWidget(
    challenge[currentTab?.widgetKey ?? OVERVIEW_TAB] ?? ([] as CType<unknown>[]),
  );
  if (!nestedTabs) return;
  nestedTabs.fields.tabUrl = getChallengeUrl(challenge, currentTab);
  const currentNested = nestedTabs.fields.tabItems.find(
    tabItem => tabItem.fields.slug === nestedTabSlug,
  );

  nestedTabs.fields.currentNestedTab = currentNested;

  // if (currentNested) {
  //   currentNested.fields.isCurrent = true;
  //   currentNested.fields.pageUrl = `${getChallengeBaseUrl(challenge)}/${
  //     currentTab?.slug
  //   }/${currentNested.fields.slug}`;
  // }
};

const parseUnnecessaryKeys: any = (data: any) => {
  if (typeof data === 'object' && data !== null) {
    if (!Array.isArray(data)) {
      return Object.keys(data).reduce((previousValue, key) => {
        return ['widget'].includes(key)
          ? previousValue
          : {
              ...previousValue,
              [key]: parseUnnecessaryKeys(data[key as keyof typeof data]),
            };
      }, {});
    }
    return data.map(item => parseUnnecessaryKeys(item));
  }
  return data;
};

const modifyChallengeData: any = (
  data: IChallengeProps,
  tabSlug: TabWidgetKeys,
  hasNestedTab: string | boolean,
) => {
  const tabWidgetKey = ['overview', 'explore', 'focus', 'imagine', 'create', 'pitch'];

  const index = tabWidgetKey.indexOf(tabSlug);
  if (index !== -1) {
    tabWidgetKey.splice(index, 1);
  }
  const newData = omit(data, tabWidgetKey);
  if (!hasNestedTab) {
    newData[tabSlug]?.forEach((value, index, array) => {
      if (value.sys.contentType.sys.id === 'tabsBlade') {
        array[index] = parseUnnecessaryKeys(value);
      }
    });
  } else {
    newData[tabSlug]?.forEach((value, index, array) => {
      if (value.sys.contentType.sys.id === 'tabsBlade') {
        const tabItemsFields = value.fields as TabsBladeProps;
        tabItemsFields.tabItems = parseUnnecessaryKeys(tabItemsFields.tabItems);
        array[index].fields = tabItemsFields;
      }
    });
  }

  return newData;
};

export const getStaticProps: GetStaticProps<IStaticPageProps<PageTypeChallenge>> = async ({
  params,
}) => {
  const slugs = params?.slug || [''];
  const challengeId = slugs[0];
  const challengeData = CACHED_CHALLENGES[challengeId];
  let pageResponse: TPageData<IChallengeProps>;

  if (!challengeData) {
    pageResponse = await getPageData<PageTypeChallenge>(slugs[0], 'challenge');
    CACHED_CHALLENGES[challengeId] = pageResponse.page;
  } else {
    const siteData = await getSiteData();
    pageResponse = { page: challengeData, site: siteData.site };
  }

  if (slugs.length === 2) setCurrentTab(slugs as string[], pageResponse.page);

  if (slugs.length === 3) setNestedTab(slugs as string[], pageResponse.page);

  let pageData = structuredClone(pageResponse.page);

  pageData = modifyChallengeData(pageData, slugs[1], slugs.length === 3 && slugs[2]);

  const res = {
    site: pageResponse.site,
    page: pageData,
    key: slugs[slugs.length - 1],
  };

  return { props: res };
};

export default WithPage(ChallengeDetailPage);
